import { useMe } from '@/api/me';
import { usePreferences } from '@/api/preferences';
import { useTripItems } from '@/api/trips';
import { TripModeToggle } from '@/components/trips/TripModeToggle';
import { progressColor } from '@/components/trips/progressColor';
import { tripProgress } from '@/components/trips/tripProgress';
import { useApplyFontScale } from '@/lib/useApplyFontScale';
import { useScrollCollapse } from '@/lib/useScrollCollapse';
import { useScrollMemory } from '@/lib/useScrollMemory';
import { cn } from '@/lib/utils';
import { useQuickAddStore } from '@/stores/quickAdd';
import { Link, useRouterState } from '@tanstack/react-router';
import { Luggage, Mic, ShieldX } from 'lucide-react';
/**
 * Layout frame (frontend-map §2.1 — FOUNDATION-owned). Wraps the active route's
 * <Outlet/>. Responsive: a top bar on all sizes; feature routes fill the content
 * area. Mobile/desktop surface tuning happens inside feature routes (PRD §5).
 */
import { type ReactNode, useEffect } from 'react';
import { AppNav } from './AppNav';
import { CommandPalette } from './CommandPalette';
import { useGlobalCommands } from './useGlobalCommands';

export function AppShell({ children }: { children: ReactNode }) {
  useApplyFontScale();
  const me = useMe();
  // Warm the account preferences once per session so toggles read instantly everywhere.
  usePreferences();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Remember the current route so an installed PWA can resume here on next launch.
  const routeHref = useRouterState({
    select: (state) => state.location.pathname + state.location.searchStr,
  });
  useEffect(() => {
    try {
      localStorage.setItem('uc-last-route', routeHref);
    } catch {
      // ignore unavailable storage
    }
  }, [routeHref]);
  // Per-page scroll memory so a PWA resume lands where the user left off, not
  // just on the same route.
  useScrollMemory(pathname);
  const tripDetailMatch = /^\/trips\/([^/]+)/.exec(pathname);
  const tripId = tripDetailMatch?.[1] ? decodeURIComponent(tripDetailMatch[1]) : null;
  const isTripDetail = tripId !== null;
  const collapsed = useScrollCollapse(isTripDetail);
  const launchVoice = useQuickAddStore((state) => state.launchVoice);
  // Navigation + app-level commands, available on every page.
  useGlobalCommands(Boolean(me.data?.isAdmin));
  const tripItems = useTripItems(tripId ?? '__none__', isTripDetail);
  const progress = isTripDetail ? tripProgress(tripItems.data ?? []) : null;
  const pct =
    progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  // A blocked user gets a dead-end screen instead of the app (the API also denies
  // every call but /me, so there'd be nothing to show anyway).
  if (me.data?.blocked) {
    return <BlockedScreen />;
  }

  return (
    <div className="flex min-h-full flex-col">
      {/* On a trip page (mobile), scrolling down slides the header UP by exactly the
          top row's height, tucking the wordmark/nav/toggle off-screen and leaving the
          progress bar pinned at the top; scrolling up / reaching the top brings it
          back. We move it with a TRANSFORM (not a height change) so the content below
          never reflows — the header just slides over/away, no scroll jump. Desktop
          (md+) is never collapsed. */}
      <header
        className={cn(
          'sticky top-0 z-40 bg-[var(--background)]/95 backdrop-blur transition-transform duration-300 ease-out supports-[backdrop-filter]:bg-[var(--background)]/60',
          !isTripDetail && 'border-b border-[var(--border)]',
          collapsed && isTripDetail && 'max-md:-translate-y-14',
        )}
      >
        <div className="relative mx-auto flex h-14 w-full max-w-6xl items-center justify-between gap-4 px-4">
          <Link to="/" className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--primary)] text-[var(--primary-foreground)]">
              <Luggage className="h-[18px] w-[18px]" />
            </span>
            {/* On a trip page the centered mode toggle needs the room, so the
                wordmark text collapses to the icon below md. */}
            <span
              className={cn(
                'truncate whitespace-nowrap font-display text-xl font-semibold tracking-tight',
                isTripDetail && 'hidden md:inline',
              )}
            >
              Ultimate Checklist
            </span>
          </Link>

          {isTripDetail ? (
            <div className="-translate-x-1/2 absolute left-1/2 flex items-center gap-2">
              <TripModeToggle />
              {/* Desktop-only voice Quick Add. On phones the nav menu's Quick add
                  pill already launches listening, and the header has no room. */}
              <button
                type="button"
                aria-label="Quick add by voice"
                title="Quick add by voice (A)"
                onClick={launchVoice}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card)] text-[var(--primary)] shadow-sm transition-colors hover:bg-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] md:inline-flex"
              >
                <Mic className="h-4 w-4" />
              </button>
            </div>
          ) : null}

          <AppNav />
        </div>
        {isTripDetail ? (
          // biome-ignore lint/a11y/useFocusableInteractive: progressbar is a read-only status role, not a focusable control
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress?.total ?? 0}
            aria-valuenow={progress?.done ?? 0}
            aria-valuetext={`${progress?.done ?? 0} of ${progress?.total ?? 0}`}
            className="h-1.5 w-full bg-[var(--muted)]"
          >
            <div
              className="h-full transition-[width,background-color] duration-300 ease-out"
              style={{ width: `${pct}%`, backgroundColor: progressColor(pct) }}
            />
          </div>
        ) : null}
      </header>
      <main
        className={cn(
          // Tighter gutters on mobile to maximize list real estate (halved < md).
          'mx-auto w-full flex-1 px-2 py-3 md:px-4 md:py-6',
          isTripDetail ? 'max-w-none 2xl:px-6' : 'max-w-6xl',
        )}
      >
        {children}
      </main>
      <CommandPalette />
    </div>
  );
}

/** Full-screen dead end for a blocked account — no nav, no app. */
function BlockedScreen() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-100 text-red-600">
        <ShieldX className="h-7 w-7" />
      </span>
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">Access blocked</h1>
        <p className="max-w-sm text-sm text-[var(--muted-foreground)]">
          Your access to Ultimate Checklist has been blocked. If you think this is a mistake, please
          reach out to the site administrator.
        </p>
      </div>
    </div>
  );
}
